import { memo, useCallback, useRef } from 'react';
import ReactPlayer from 'react-player';

import { LEARNING_RESOURCE_TYPE } from '@didaskill/domain/learning-resources/constants';
import type { LearningResource } from '@didaskill/domain/learning-resources/types';
import type { Upload } from '@didaskill/domain/uploads/types';
import { FileDownload } from '@didaskill/ui/design-system';
import { ComponentSwitch } from '@didaskill/ui/utils';

import * as styles from './ResourceDisplay.styles';

type ResourceDisplayProps = {
  resource: LearningResource;
  onDownloadFile: (file: Upload) => void;
  isDownloading?: boolean;
  onPlayVideo?: () => Promise<void>;
};

export const ResourceDisplayBase = ({
  resource,
  onDownloadFile,
  isDownloading,
  onPlayVideo,
}: ResourceDisplayProps) => {
  const hasCountedView = useRef(false);

  /**
   * Records a single view for this player instance.
   * Seek/pause resume fire `onPlay`/`onStart` again and must not increment the counter.
   * @returns Nothing.
   */
  const handleStartVideo = useCallback((): void => {
    if (!onPlayVideo || hasCountedView.current) {
      return;
    }

    hasCountedView.current = true;
    void onPlayVideo();
  }, [onPlayVideo]);

  return (
    <ComponentSwitch value={resource.type}>
      <ComponentSwitch.Item value={LEARNING_RESOURCE_TYPE.VIDEO}>
        <ReactPlayer
          url={resource.href}
          width="auto"
          css={styles.videoPlayer}
          controls
          onStart={handleStartVideo}
        />
      </ComponentSwitch.Item>

      <ComponentSwitch.Item value={LEARNING_RESOURCE_TYPE.REPLAY}>
        <ReactPlayer
          url={resource.href}
          width="auto"
          css={styles.videoPlayer}
          controls
          onStart={handleStartVideo}
        />
      </ComponentSwitch.Item>

      <ComponentSwitch.Item value={LEARNING_RESOURCE_TYPE.FILE}>
        <FileDownload
          file={resource.upload}
          onDownload={onDownloadFile}
          isLoading={isDownloading}
        />
      </ComponentSwitch.Item>

      <ComponentSwitch.Item value={LEARNING_RESOURCE_TYPE.AUDIO}>
        <FileDownload
          file={resource.upload}
          onDownload={onDownloadFile}
          isLoading={isDownloading}
        />
      </ComponentSwitch.Item>
    </ComponentSwitch>
  );
};

ResourceDisplayBase.displayName = 'ResourceDisplay';

export const ResourceDisplay = memo(ResourceDisplayBase);
